/* Activity — what has been done to this salon from outside it.
 *
 * The reason this log exists is a sentence somebody will eventually say: "the
 * board changed on its own". It did not; somebody changed it from here, and this
 * is the page that says who and when. Without it, an admin app is a way to
 * modify a customer's configuration with no record that anyone did.
 *
 * Two writers feed it — `admin_log()` for anything this browser does, and the
 * edge function's own insert for the things only it can do. Both take the actor
 * from a verified session rather than from the request, so a row here cannot be
 * about somebody else. See 10-platform-admins.sql.
 *
 * Nothing on this page can edit or delete a row: `admin_audit` has a read policy
 * and no other, and no function offers a delete. That is the point of it.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { nav } from '../nav.js';
import { store } from '../store.js';

var Activity = {
  name: 'AdminActivity',
  setup: function () {
    var rows = Vue.ref([]);
    var loaded = Vue.ref(false);
    var open = Vue.reactive({});
    var actionFilter = Vue.ref('');

    async function load() {
      var id = store.selectedId.value;
      if (!id) return;
      loaded.value = false;
      var res = await api.query('Loading activity', function (sb) {
        return sb.from('admin_audit').select('*')
          .eq('tenant_id', id).order('at', { ascending: false }).limit(200);
      });
      if (res) rows.value = res;
      loaded.value = true;
    }

    Vue.watch(store.selectedId, load, { immediate: true });

    // The actions actually present, so the filter offers what happened rather
    // than a fixed list of everything that could.
    var actions = Vue.computed(function () {
      var seen = {};
      rows.value.forEach(function (r) { seen[r.action] = true; });
      return Object.keys(seen).sort();
    });

    var visible = Vue.computed(function () {
      if (!actionFilter.value) return rows.value;
      return rows.value.filter(function (r) { return r.action === actionFilter.value; });
    });

    function detail(row) {
      try {
        return JSON.stringify(row.detail, null, 2);
      } catch (e) {
        return String(row.detail);
      }
    }

    return {
      rows: rows,
      visible: visible,
      actions: actions,
      actionFilter: actionFilter,
      loaded: loaded,
      open: open,
      detail: detail,
      reload: load,
      stamp: store.stamp,
      ago: store.ago,
      goPage: nav.goPage
    };
  },
  template: [
    '<div class="page">',
    '  <div class="page__head">',
    '    <h2>Activity</h2>',
    '    <p class="page__lead">Every change made to this salon from the admin app, with who',
    '      made it. Nothing the salon does to itself appears here &mdash; this is a record of',
    '      us, not of them.</p>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <div class="card__row">',
    '      <label class="field">',
    '        <span class="field__label">Action</span>',
    '        <select v-model="actionFilter" class="input">',
    '          <option value="">All</option>',
    '          <option v-for="a in actions" :key="a" :value="a">{{ a }}</option>',
    '        </select>',
    '      </label>',
    '      <button type="button" class="btn btn--ghost btn--sm" @click="reload()">Reload</button>',
    '      <span class="hint hint--inline">Last 200 entries.</span>',
    '    </div>',
    '  </div>',
    '',
    '  <p v-if="!loaded" class="empty">Loading…</p>',
    '  <p v-else-if="!rows.length" class="empty">Nothing has been changed on this salon from',
    '    the admin app.</p>',
    '',
    '  <ul v-else class="rows rows--tight">',
    '    <li v-for="r in visible" :key="r.id" class="row row--stack">',
    '      <button type="button" class="row__head" @click="open[r.id] = !open[r.id]">',
    '        <span class="log__at" :title="stamp(r.at)">{{ ago(r.at) }}</span>',
    '        <span class="log__action mono">{{ r.action }}</span>',
    '        <span class="log__who">{{ r.actor_email || "(deleted account)" }}</span>',
    '        <span class="row__edit">{{ open[r.id] ? "Hide" : "Detail" }}</span>',
    '      </button>',
    '      <pre v-if="open[r.id]" class="pre mono">{{ detail(r) }}</pre>',
    '    </li>',
    '  </ul>',
    '</div>'
  ].join('\n')
};

nav.register({
  id: 'activity',
  name: 'Activity',
  group: 'Advanced',
  page: Activity
});

export { Activity };
